import React, { useState, useEffect } from 'react';
import { Plus, Trash2, X, Shield, ChevronRight } from 'react-feather';
import { API_BASE_URL } from '../config/api';
import PageContainer from '../components/PageContainer';
import '../styles/guardrails.css';

// ─── Guardrail type definitions ────────────────────────────────────────────────
const GUARDRAIL_TYPES = {
  anonymize: {
    label: 'PII Anonymizer',
    description: 'Detects and redacts personally identifiable information (names, emails, phone numbers, SSNs) before it reaches the LLM.',
    badge: 'Input',
    badgeColor: 'badge-input',
    defaultConfig: {},
  },
  ban_competitors: {
    label: 'Ban Competitors',
    description: 'Blocks or redacts mentions of competitor products/companies in user messages or agent responses.',
    badge: 'Input / Output',
    badgeColor: 'badge-both',
    defaultConfig: { competitors: [], redact: false, threshold: 0.5 },
  },
  regex: {
    label: 'Regex Filter',
    description: 'Blocks content matching custom regular expression patterns, useful for phone numbers, credit cards, or any custom format.',
    badge: 'Input',
    badgeColor: 'badge-input',
    defaultConfig: { patterns: [], redact: false },
  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Per-type config editor ────────────────────────────────────────────────────
function BanCompetitorsConfig({ config, onChange }) {
  const [input, setInput] = useState('');
  const competitors = config.competitors || [];

  const add = () => {
    const v = input.trim();
    if (!v || competitors.includes(v)) return;
    onChange({ ...config, competitors: [...competitors, v] });
    setInput('');
  };

  const remove = (c) =>
    onChange({ ...config, competitors: competitors.filter((x) => x !== c) });

  return (
    <div className="gr-config-block">
      <div className="gr-config-row">
        <label>Competitor names</label>
        <div className="gr-tag-input-row">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
            placeholder="e.g. OpenAI, Salesforce…"
          />
          <button type="button" className="gr-btn-add" onClick={add}>
            <Plus size={14} /> Add
          </button>
        </div>
        <div className="gr-tags">
          {competitors.map((c) => (
            <span key={c} className="gr-tag">
              {c}
              <button type="button" onClick={() => remove(c)}>
                <X size={12} />
              </button>
            </span>
          ))}
          {competitors.length === 0 && (
            <span className="gr-tag-empty">No competitors added yet.</span>
          )}
        </div>
      </div>

      <div className="gr-config-row gr-config-row--inline">
        <label>Redact instead of block</label>
        <div className="gr-switch-mini">
          <input
            type="checkbox"
            id="bc-redact"
            checked={!!config.redact}
            onChange={(e) => onChange({ ...config, redact: e.target.checked })}
          />
          <label htmlFor="bc-redact" />
        </div>
        <span className="gr-hint">
          {config.redact ? 'Competitor names will be replaced with [REDACTED].' : 'Messages will be blocked entirely.'}
        </span>
      </div>

      <div className="gr-config-row">
        <label>Detection threshold &nbsp;<span className="gr-value-badge">{config.threshold ?? 0.5}</span></label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={config.threshold ?? 0.5}
          onChange={(e) => onChange({ ...config, threshold: parseFloat(e.target.value) })}
          className="gr-slider"
        />
        <div className="gr-slider-labels"><span>0, lenient</span><span>1, strict</span></div>
      </div>
    </div>
  );
}

function RegexConfig({ config, onChange }) {
  const [input, setInput] = useState('');
  const patterns = config.patterns || [];

  const add = () => {
    const v = input.trim();
    if (!v || patterns.includes(v)) return;
    onChange({ ...config, patterns: [...patterns, v] });
    setInput('');
  };

  const remove = (p) =>
    onChange({ ...config, patterns: patterns.filter((x) => x !== p) });

  return (
    <div className="gr-config-block">
      <div className="gr-config-row">
        <label>Regex patterns</label>
        <div className="gr-tag-input-row">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
            placeholder="e.g. \d{16} for credit card numbers"
            className="gr-mono"
          />
          <button type="button" className="gr-btn-add" onClick={add}>
            <Plus size={14} /> Add
          </button>
        </div>
        <div className="gr-tags">
          {patterns.map((p) => (
            <span key={p} className="gr-tag gr-tag--mono">
              {p}
              <button type="button" onClick={() => remove(p)}>
                <X size={12} />
              </button>
            </span>
          ))}
          {patterns.length === 0 && (
            <span className="gr-tag-empty">No patterns added yet.</span>
          )}
        </div>
      </div>

      <div className="gr-config-row gr-config-row--inline">
        <label>Redact instead of block</label>
        <div className="gr-switch-mini">
          <input
            type="checkbox"
            id="rx-redact"
            checked={!!config.redact}
            onChange={(e) => onChange({ ...config, redact: e.target.checked })}
          />
          <label htmlFor="rx-redact" />
        </div>
        <span className="gr-hint">
          {config.redact ? 'Matching content will be replaced with [REDACTED].' : 'Messages containing matches will be blocked.'}
        </span>
      </div>
    </div>
  );
}

// ─── Creation Modal ────────────────────────────────────────────────────────────
function CreateModal({ onClose, onCreate }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'configure'
  const [selectedType, setSelectedType] = useState(null);
  const [name, setName] = useState('');
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const pickType = (type) => {
    setSelectedType(type);
    setName(GUARDRAIL_TYPES[type].label);
    setConfig({ ...GUARDRAIL_TYPES[type].defaultConfig });
    setStep('configure');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!name.trim()) { setError('Name is required.'); return; }
    try {
      setSaving(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/guardrails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({
          name: name.trim(),
          guardrail_type: selectedType,
          config,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to create guardrail');
      onCreate(data);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="gr-modal-backdrop">
      <div className="gr-modal">
        <div className="gr-modal-header">
          <h2>{step === 'pick' ? 'Choose Guardrail Type' : `Configure ${GUARDRAIL_TYPES[selectedType]?.label}`}</h2>
          <button className="gr-icon-btn" onClick={onClose}><X size={18} /></button>
        </div>

        {step === 'pick' ? (
          <div className="gr-type-grid">
            {Object.entries(GUARDRAIL_TYPES).map(([key, def]) => (
              <button key={key} className="gr-type-card" onClick={() => pickType(key)}>
                <div className="gr-type-card-top">
                  <Shield size={22} className="gr-type-icon" />
                  <span className={`gr-badge ${def.badgeColor}`}>{def.badge}</span>
                </div>
                <div className="gr-type-label">{def.label}</div>
                <div className="gr-type-desc">{def.description}</div>
                <div className="gr-type-arrow"><ChevronRight size={16} /></div>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="gr-create-form">
            {error && <div className="gr-error">{error}</div>}

            <div className="gr-form-field">
              <label>Guardrail name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My guardrail"
                required
              />
            </div>

            {selectedType === 'anonymize' && (
              <div className="gr-config-block">
                <p className="gr-info">
                  The PII Anonymizer automatically detects names, email addresses, phone numbers, SSNs, and other personal data using NLP, no additional configuration required.
                </p>
              </div>
            )}

            {selectedType === 'ban_competitors' && (
              <BanCompetitorsConfig config={config} onChange={setConfig} />
            )}

            {selectedType === 'regex' && (
              <RegexConfig config={config} onChange={setConfig} />
            )}

            <div className="gr-modal-actions">
              <button type="button" className="gr-btn-secondary" onClick={() => setStep('pick')} disabled={saving}>
                Back
              </button>
              <button type="submit" className={`gr-btn-primary ${saving ? 'is-loading' : ''}`} disabled={saving}>
                {saving ? 'Creating…' : 'Create Guardrail'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Detail Panel ──────────────────────────────────────────────────────────────
function DetailPanel({ guardrail }) {
  const def = GUARDRAIL_TYPES[guardrail.guardrail_type] || {};
  const cfg = guardrail.config || {};

  return (
    <div className="gr-detail">
      <div className="gr-detail-header">
        <Shield size={20} />
        <div>
          <div className="gr-detail-name">{guardrail.name}</div>
          <span className={`gr-badge ${def.badgeColor}`}>{def.badge || guardrail.guardrail_type}</span>
        </div>
      </div>

      <div className="gr-detail-section">
        <div className="gr-detail-label">Type</div>
        <div className="gr-detail-value">{def.label || guardrail.guardrail_type}</div>
        <div className="gr-detail-desc">{def.description}</div>
      </div>

      {guardrail.guardrail_type === 'ban_competitors' && (
        <>
          <div className="gr-detail-section">
            <div className="gr-detail-label">Competitors blocked</div>
            <div className="gr-tags gr-tags--readonly">
              {(cfg.competitors || []).length === 0
                ? <span className="gr-tag-empty">None configured</span>
                : (cfg.competitors || []).map((c) => <span key={c} className="gr-tag">{c}</span>)}
            </div>
          </div>
          <div className="gr-detail-section">
            <div className="gr-detail-label">Mode</div>
            <div className="gr-detail-value">{cfg.redact ? 'Redact' : 'Block'}</div>
          </div>
          <div className="gr-detail-section">
            <div className="gr-detail-label">Threshold</div>
            <div className="gr-detail-value">{cfg.threshold ?? 0.5}</div>
          </div>
        </>
      )}

      {guardrail.guardrail_type === 'regex' && (
        <>
          <div className="gr-detail-section">
            <div className="gr-detail-label">Patterns</div>
            <div className="gr-tags gr-tags--readonly">
              {(cfg.patterns || []).length === 0
                ? <span className="gr-tag-empty">None configured</span>
                : (cfg.patterns || []).map((p) => <span key={p} className="gr-tag gr-tag--mono">{p}</span>)}
            </div>
          </div>
          <div className="gr-detail-section">
            <div className="gr-detail-label">Mode</div>
            <div className="gr-detail-value">{cfg.redact ? 'Redact' : 'Block'}</div>
          </div>
        </>
      )}

      <div className="gr-detail-section">
        <div className="gr-detail-label">Guardrail ID</div>
        <div className="gr-detail-value gr-mono">{guardrail.id}</div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
const Guardrails = () => {
  const [guardrails, setGuardrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchGuardrails = async () => {
    try {
      setLoading(true);
      setListError(null);
      const res = await fetch(`${API_BASE_URL}/api/guardrails`, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      });
      if (!res.ok) throw new Error('Failed to fetch guardrails');
      const data = await res.json();
      setGuardrails(data);
      if (selected) {
        const still = data.find((g) => g.id === selected.id);
        if (!still) setSelected(null);
      }
    } catch (err) {
      setListError(err.message || 'Failed to load guardrails.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGuardrails(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreated = async () => {
    await fetchGuardrails();
    // Select the last item after the refetch
    setSelected(null); // will be set after fetch completes via effect below
  };

  const handleDelete = async (gr) => {
    if (!window.confirm(`Delete guardrail "${gr.name}"? This cannot be undone.`)) return;
    try {
      setDeletingId(gr.id);
      const res = await fetch(`${API_BASE_URL}/api/guardrails/${gr.id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete');
      }
      setGuardrails((prev) => prev.filter((g) => g.id !== gr.id));
      if (selected?.id === gr.id) setSelected(null);
    } catch (err) {
      setListError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PageContainer
      loading={loading}
      loadingMessage="Loading guardrails…"
      title="Guardrails"
      subtitle="Create and manage input/output safety guardrails for your agents"
    >
      {listError && <div className="save-message error">{listError}</div>}

      <div className="gr-header-row">
        <button className="add-knowledge-button" onClick={() => setShowCreate(true)}>
          <Plus size={20} />
        </button>
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreated}
        />
      )}

      <div className="gr-layout">
        {/* Left: list */}
        <div className="gr-list">
          <h3>Your Guardrails</h3>
          {guardrails.length === 0 && !loading && (
            <p className="empty-state">No guardrails yet. Click + to create one.</p>
          )}
          {guardrails.map((gr) => {
            const def = GUARDRAIL_TYPES[gr.guardrail_type] || {};
            return (
              <div
                key={gr.id}
                className={`gr-list-item${selected?.id === gr.id ? ' selected' : ''}`}
                onClick={() => setSelected(gr)}
              >
                <div className="gr-list-item-main">
                  <Shield size={15} className="gr-list-icon" />
                  <div>
                    <div className="gr-list-name">{gr.name}</div>
                    <div className="gr-list-meta">
                      <span className={`gr-badge gr-badge--sm ${def.badgeColor}`}>
                        {def.badge || gr.guardrail_type}
                      </span>
                      &nbsp;{def.label || gr.guardrail_type}
                    </div>
                  </div>
                </div>
                <button
                  className="gr-icon-btn gr-icon-btn--danger"
                  onClick={(e) => { e.stopPropagation(); handleDelete(gr); }}
                  disabled={deletingId === gr.id}
                  title="Delete"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Right: detail */}
        <div className="gr-detail-pane">
          {selected
            ? <DetailPanel guardrail={selected} />
            : <div className="empty-state">Select a guardrail to view its configuration.</div>}
        </div>
      </div>
    </PageContainer>
  );
};

export default Guardrails;
